import { sendNotification, useNotification, useProducts } from '@gozoki/api'
import type { Product } from '@gozoki/api-types'
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import z from 'zod/v3'
import { AuthorizedButton } from '~/components/communication/AuthorizedButton'
import type { PermissionTuple } from '~/utils/ability'
import { withRoutePermissions } from '~/utils/rbac'
import { useToast } from '../../../components/communication/Toast'
import Page from '../../../components/Page'
import { StoresPicker } from '../../../utils/dashboard/storesPicker'
import { getErrorMessageWithSentry } from '../../../utils/sentry'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

const SEND: PermissionTuple = ['add', 'notifications.notifications.notification']
const DUPLICATE: PermissionTuple = ['add', 'notifications.notifications.duplication']

// L'écran sert la création ET la duplication (`?notificationId=`) : il s'ouvre avec l'un OU l'autre
// droit, le bouton « Envoyer » exigeant ensuite celui du mode réellement en cours.
export const Route = createFileRoute('/_authenticated/notifications/new')({
  staticData: {
    permission: { any: [SEND, DUPLICATE] },
  },
  component: CreateNotificationPage,
  validateSearch: z.object({
    notificationId: z.number().optional(),
  }),
})
withRoutePermissions(Route)

const actions = [
  { label: 'Aucune action', value: 'none' },
  { label: 'Renvoyer vers la page Anti-gaspi', value: 'anti-gaspi' },
  { label: 'Renvoyer vers un produit anti-gaspi', value: 'product-anti-gaspi' },
  { label: 'Renvoyer vers un produit', value: 'product' },
]

// Mirror the backend kill switch (see apps/gozoki-backend/app/Controllers/Http/notifications/sendNotification.ts).
// Set both to true together when re-enabling broadcasts.
const BROADCAST_ENABLED = false

function CreateNotificationPage() {
  const navigate = Route.useNavigate()
  const { notificationId } = Route.useSearch()

  const { data: products } = useProducts()

  const isDuplicateMode = !!notificationId

  const { data: notification, isLoading } = useNotification(notificationId)

  const [Toast, showToast] = useToast()
  const [message, setMessage] = useState('')
  const [title, setTitle] = useState('')
  const [subTitle, setSubTitle] = useState<string | null>(null)
  const [onlyUsers, setOnlyUsers] = useState(false)
  const [shopIds, setShopIds] = useState<number[]>([])
  const [action, setAction] = useState<(typeof actions)[0]>(actions[0])
  const [product, setProduct] = useState<Product | null>(null)
  const [sending, setSending] = useState(false)

  const cancel = useCallback(() => {
    // Reset();
    navigate({ to: '/notifications' })
  }, [navigate])

  const navigateBack = cancel

  useEffect(() => {
    if (isDuplicateMode && notification) {
      setTitle(notification.title)
      setSubTitle(notification.subTitle)
      setMessage(notification.text)
      setOnlyUsers(notification.onlyUsers)
      setShopIds(notification.shopIds)
      // If no action was specified, don't bother setting it
      if (!notification.data?.path) {
        return
      }

      // Find the action that matches the notification path
      let productId: number | null = null
      if (notification.data.path.startsWith('anti-gaspi/')) {
        setAction(actions[2])
        productId = parseInt(notification.data.path.split('/')[1], 10)
      } else if (notification.data.path === 'anti-gaspi') {
        setAction(actions[1])
      } else if (notification.data.path === 'product') {
        setAction(actions[3])
        productId = parseInt(notification.data.path.split('/')[1], 10)
      }
      if (productId && products) {
        setProduct(products.find((p) => p.id === productId) ?? null)
      }
    }
  }, [notification, isLoading, isDuplicateMode, products])

  const path = useMemo(() => {
    if (action.value === 'product-anti-gaspi' && product) {
      return `anti-gaspi/${product.id}`
    }
    if (action.value === 'anti-gaspi') {
      return 'anti-gaspi'
    }
    if (action.value === 'product' && product) {
      return `produit/${product.id}`
    }
    return null
  }, [action, product])

  const { mutate } = useMutation({
    mutationFn: () =>
      sendNotification({
        title,
        subTitle: subTitle || null,
        text: message,
        onlyUsers,
        shopIds,
        data: {
          path,
        },
      }),
    onError: (error) => {
      setSending(false)
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      setSending(false)
      showToast({ message: 'Notification envoyée', severity: 'success' })
      navigateBack()
    },
  })

  return (
    <Page>
      <Toast />
      {BROADCAST_ENABLED ? null : (
        <Alert severity="warning" sx={{ marginBottom: '16px' }}>
          L&apos;envoi de notifications broadcast est désactivé en attendant la fin de
          l&apos;investigation sur le broadcast non-autorisé du 20 mai (« Paiement refusé » envoyé à
          ~tous les utilisateurs). Contacte l&apos;équipe backend pour réactiver.
        </Alert>
      )}
      <div style={{ display: 'flex', paddingLeft: 10 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Envoyer une notification
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={cancel}
          disabled={sending}
        >
          Annuler
        </Button>

        <AuthorizedButton
          permission={isDuplicateMode ? DUPLICATE : SEND}
          legacyRights={['ADMIN']}
          variant="contained"
          color="primary"
          onClick={() => {
            setSending(true)
            mutate()
          }}
          disabled={!BROADCAST_ENABLED || !message || !title || sending}
        >
          Envoyer
        </AuthorizedButton>
      </div>
      <Paper
        sx={{
          marginTop: '16px',
          padding: '24px',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Type d'utilisateur
              </Typography>
              <Box display="flex" alignItems="center">
                <Switch
                  checked={onlyUsers}
                  onChange={() => setOnlyUsers(!onlyUsers)}
                  name="Uniquement les utilisateurs inscrits"
                />
                <Typography>Uniquement les utilisateurs inscrits</Typography>
              </Box>
            </Grid>
            {onlyUsers ? (
              <Grid item flex={1}>
                <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                  Utilisateurs du magasin
                </Typography>
                <StoresPicker onlyBoutiques setCurrentStores={setShopIds} />
              </Grid>
            ) : null}
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Titre de la notification
              </Typography>
              <TextField fullWidth value={title} onChange={(e) => setTitle(e.target.value)} />
            </Grid>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Sous-titre (pour IOS uniquement)
              </Typography>
              <TextField fullWidth value={subTitle} onChange={(e) => setSubTitle(e.target.value)} />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Message
              </Typography>
              <TextField
                fullWidth
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                InputProps={{
                  style: {
                    alignItems: 'flex-start',
                    minHeight: '102px',
                    textAlign: 'end',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Action
              </Typography>
              <Select
                fullWidth
                value={action.value}
                onChange={(e) =>
                  setAction(actions.find((a) => a.value === e.target.value) ?? actions[0])
                }
              >
                {actions.map((a) => (
                  <MenuItem key={a.value} value={a.value}>
                    {a.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            {['product-anti-gaspi', 'product'].includes(action.value) ? (
              <Grid item flex={1}>
                <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                  Sélectionner le produit
                </Typography>
                <Select
                  fullWidth
                  value={product?.id}
                  onChange={(e) =>
                    setProduct(products?.find((p) => p.id === e.target.value) ?? null)
                  }
                >
                  {products?.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </Paper>
    </Page>
  )
}
